import React, { useEffect, useMemo, useState } from "react";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { getSettings } from "../../../../services/settingsServices";
import { getAllReglementInscriptionBySaison } from "../../../../services/reglementinscriptionServices";

import DashboardComptaHeader from "./components/DashboardComptaHeader";
import ComptaKpiCards from "./components/ComptaKpiCards";
import ComptaCollapsibleSection from "./components/ComptaCollapsibleSection";
import PaymentStatusChart from "./components/PaymentStatusChart";
import PaymentTypeChart from "./components/PaymentTypeChart";
import BoutiqueSalesPanel from "./components/BoutiqueSalesPanel";
import MonthlyPurchasesChart from "./components/MonthlyPurchasesChart";

import {
  FaChartLine,
  FaChartPie,
  FaCreditCard,
  FaShoppingBag,
  FaSyncAlt,
} from "react-icons/fa";

const TYPES_PAIEMENT_POSSIBLES = [
  "En ligne",
  "Chèque",
  "Espèces",
  "Virement",
  "CB",
  "Autre",
  "Non renseigné",
];

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function unwrapSettings(settings) {
  if (!settings) return {};

  let output = settings;

  if (Array.isArray(output)) output = output[0] || {};
  if (output?.dataValues) output = output.dataValues;

  return output || {};
}

function prevSeason(season) {
  const [start, end] = String(season || "")
    .split("-")
    .map(Number);

  if (!start || !end) return "";

  return `${start - 1}-${end - 1}`;
}

function nextSeason(season) {
  const [start, end] = String(season || "")
    .split("-")
    .map(Number);

  if (!start || !end) return "";

  return `${start + 1}-${end + 1}`;
}

function toAmount(value) {
  const number = Number.parseFloat(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function calculateComptaStats(reglements) {
  let licences = 0;
  let cotisationsBrutes = 0;
  let reductions = 0;
  let passSport = 0;

  const statutCounts = {};
  const typeCounts = {};

  for (const reglement of reglements) {
    const montantLicence = toAmount(reglement?.montant_lic_assurance);
    const montantAdhesion = toAmount(reglement?.montant_adhesion);
    const montantCotisation = toAmount(reglement?.montant_cotisation);

    const reductionFoyer = Math.abs(toAmount(reglement?.reduction_foyer));
    const reductionSaison = Math.abs(toAmount(reglement?.reduction_saison));
    const montantPassSport = Math.abs(toAmount(reglement?.pass_sport));

    licences += montantLicence;
    cotisationsBrutes += montantCotisation + montantAdhesion;
    reductions += reductionFoyer + reductionSaison + montantPassSport;
    passSport += montantPassSport;

    const statut = String(
      reglement?.statut_reglement || "Non renseigné",
    ).trim();
    statutCounts[statut] = (statutCounts[statut] || 0) + 1;

    const type = String(reglement?.type_reglement || "Non renseigné").trim();
    typeCounts[type || "Non renseigné"] =
      (typeCounts[type || "Non renseigné"] || 0) + 1;
  }

  const cotisationsNettes = cotisationsBrutes - reductions;
  const totalReglements = licences + cotisationsNettes;
  const nombrePassSport = passSport ? Math.round(passSport / 50) : 0;

  const totalLignes = reglements.length || 1;

  const statutReglements = Object.entries(statutCounts)
    .map(([statut, count]) => ({
      statut,
      count,
      percent: Number(((count / totalLignes) * 100).toFixed(1)),
    }))
    .sort((a, b) => b.count - a.count);

  const typeReglements = TYPES_PAIEMENT_POSSIBLES.map((type) => {
    const count = typeCounts[type] || 0;

    return {
      type,
      count,
      percent: Number(((count / totalLignes) * 100).toFixed(1)),
    };
  }).filter((item) => item.count > 0 || item.type === "Non renseigné");

  return {
    licences,
    cotisationsBrutes,
    cotisationsNettes,
    reductions,
    totalReglements,
    passSport,
    nombrePassSport,
    nombreReglements: reglements.length,
    statutReglements,
    typeReglements,
  };
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Dashboard comptable
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function DashboardCompta() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");

  const [reglements, setReglements] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingReglements, setLoadingReglements] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [openSections, setOpenSections] = useState({
    statuts: true,
    types: true,
    achatsMensuels: true,
    boutique: true,
  });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const fetchSettings = async () => {
    try {
      setLoadingSettings(true);

      const response = await getSettings();
      const settings = unwrapSettings(response);
      const activeSeason = settings?.saison_active || "";

      if (activeSeason) {
        setSeasons(
          [
            prevSeason(activeSeason),
            activeSeason,
            nextSeason(activeSeason),
          ].filter(Boolean),
        );

        setSelectedSeason(activeSeason);
      }
    } catch (error) {
      console.error("Erreur chargement paramètres compta :", error);
      setErrorMsg("Impossible de récupérer la saison active du club.");
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchReglements = async (season) => {
    if (!season) return;

    try {
      setLoadingReglements(true);
      setErrorMsg("");

      const response = await getAllReglementInscriptionBySaison(season);

      setReglements(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur chargement règlements compta :", error);
      setReglements([]);
      setErrorMsg("Impossible de récupérer les règlements de la saison.");
    } finally {
      setLoadingReglements(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (selectedSeason) {
      fetchReglements(selectedSeason);
    }
  }, [selectedSeason]);

  const stats = useMemo(() => {
    return calculateComptaStats(reglements);
  }, [reglements]);

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardComptaHeader />

        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Saison comptable
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Sélection de la saison
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Les indicateurs ci-dessous sont recalculés à chaque changement
                  de saison.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <select
                  value={selectedSeason}
                  onChange={(event) => setSelectedSeason(event.target.value)}
                  disabled={loadingSettings}
                  className="min-w-[220px] rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                >
                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      Saison {season}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => fetchReglements(selectedSeason)}
                  disabled={loadingReglements || !selectedSeason}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaSyncAlt
                    className={loadingReglements ? "animate-spin" : ""}
                  />
                  Actualiser
                </button>
              </div>
            </div>

            {errorMsg ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
                {errorMsg}
              </div>
            ) : null}
          </div>
        </div>

        <ComptaKpiCards stats={stats} loading={loadingReglements} />

        <ComptaCollapsibleSection
          eyebrow="Règlements"
          title="Répartition des statuts de règlement"
          description="Visualisez les dossiers réglés, en attente, partiels ou non renseignés."
          icon={<FaChartPie />}
          isOpen={openSections.statuts}
          onToggle={() => toggleSection("statuts")}
        >
          <PaymentStatusChart
            data={stats.statutReglements}
            loading={loadingReglements}
          />
        </ComptaCollapsibleSection>

        <ComptaCollapsibleSection
          eyebrow="Paiements"
          title="Répartition des types de paiement"
          description="Analyse des paiements par type : en ligne, chèque, espèces, virement, CB..."
          icon={<FaCreditCard />}
          isOpen={openSections.types}
          onToggle={() => toggleSection("types")}
        >
          <PaymentTypeChart
            data={stats.typeReglements}
            loading={loadingReglements}
          />
        </ComptaCollapsibleSection>

        <ComptaCollapsibleSection
          eyebrow="Achats"
          title="Évolution mensuelle des achats"
          description="Visualisez les pics d’achats mois par mois sur la saison sélectionnée."
          icon={<FaChartLine />}
          isOpen={openSections.achatsMensuels}
          onToggle={() => toggleSection("achatsMensuels")}
        >
          <MonthlyPurchasesChart
            selectedSeason={selectedSeason}
            seasons={seasons}
            onSeasonChange={setSelectedSeason}
          />
        </ComptaCollapsibleSection>

        <ComptaCollapsibleSection
          eyebrow="Boutique"
          title="Achats boutique par article"
          description="Analyse des ventes boutique de la saison, hors acomptes."
          icon={<FaShoppingBag />}
          isOpen={openSections.boutique}
          onToggle={() => toggleSection("boutique")}
        >
          <BoutiqueSalesPanel selectedSeason={selectedSeason} />
        </ComptaCollapsibleSection>
      </div>
    </section>
  );
}
